var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useState, useEffect, useCallback, useMemo } from "react";
import isEqual from "lodash/isEqual";
import { useHistory, useLocation } from "react-router-dom";
import { useOkapiKy } from "@folio/stripes/core";
import { useQuery } from "react-query";
import { fetchPublisherPrefixes } from "../api/productNumberService";
import { RoleEnum } from "../types/RoleEnum";
import { hasRole, isPublisher } from "../utils/permissionsUtils";
import { useDateInputValidity } from "./useDateInputValidity";
import { usePublisherAutoFill } from "./usePublisherAutoFill";
import { usePublisherBySourceId } from "./usePublisherBySourceId";
import { appendDateRangeParams, areDateRangesValid, dateRangeFiltersFromParams, EMPTY_DATE_RANGE_FILTERS, hasActiveDateRangeFilter, pickDateRangeFilters, } from "../utils/dateRangeFilters";
var EMPTY_FILTERS = __assign({ publisherId: "", publisherPrefixId: "", value: "", blocked: undefined }, EMPTY_DATE_RANGE_FILTERS);
/**
 * The applied filters as the URL holds them — the publisher by its sourceId.
 *
 * @param search `location.search` of the list
 */
var filtersFromSearch = function (search) {
    var params = new URLSearchParams(search);
    var blocked = params.get("blocked");
    return __assign({ publisherId: params.get("publisherId") || "", publisherPrefixId: params.get("publisherPrefixId") || "", value: params.get("value") || "", blocked: blocked === "true" ? true : blocked === "false" ? false : undefined }, dateRangeFiltersFromParams(params));
};
var hasAnySearchParam = function (search) {
    return Array.from(new URLSearchParams(search).keys()).length > 0;
};
/** A publisher user (not an agency worker) sees their own numbers by default. */
var shouldAutoFillPublisher = function (roles) {
    return !!roles && isPublisher(roles) && !hasRole(roles, RoleEnum.AGENCY_WORKER);
};
export var useProductNumberFilters = function () {
    var history = useHistory();
    var location = useLocation();
    var ky = useOkapiKy();
    // Initialised from the URL in the first render: filters patched in by an
    // effect afterwards would change the search and send it twice.
    var urlFilters = useState(function () { return filtersFromSearch(location.search); })[0];
    var _a = useState(urlFilters), appliedFilters = _a[0], setAppliedFilters = _a[1];
    var _b = useState(urlFilters.publisherId), temporaryPublisherFilter = _b[0], setTemporaryPublisherFilter = _b[1];
    var _c = useState(urlFilters.publisherPrefixId), temporaryPublisherPrefixFilter = _c[0], setTemporaryPublisherPrefixFilter = _c[1];
    var _d = useState(urlFilters.value), temporaryProductNumberFilter = _d[0], setTemporaryProductNumberFilter = _d[1];
    var _e = useState(urlFilters.blocked), temporaryBlockedFilter = _e[0], setTemporaryBlockedFilter = _e[1];
    var _f = useState(""), publisherSearchQuery = _f[0], setPublisherSearchQuery = _f[1];
    var _g = useState(function () { return pickDateRangeFilters(urlFilters); }), temporaryDateRangeFilters = _g[0], setTemporaryDateRangeFilters = _g[1];
    var dateInputs = useDateInputValidity();
    var resetDateInputs = dateInputs.resetInputs;
    var _h = usePublisherBySourceId(temporaryPublisherFilter || undefined), selectedPublisherData = _h.data, selectedPublisherLoading = _h.isLoading;
    var _j = useQuery(["publisherPrefixes", selectedPublisherData === null || selectedPublisherData === void 0 ? void 0 : selectedPublisherData.id], function () {
        return fetchPublisherPrefixes(ky, {
            publisherId: selectedPublisherData === null || selectedPublisherData === void 0 ? void 0 : selectedPublisherData.id,
        });
    }, {
        enabled: !!(selectedPublisherData === null || selectedPublisherData === void 0 ? void 0 : selectedPublisherData.id),
        staleTime: 30000,
    }), publisherPrefixesData = _j.data, publisherPrefixesLoading = _j.isLoading;
    // The search filters by the publisher's internal id, the URL holds its sourceId.
    var _k = usePublisherBySourceId(appliedFilters.publisherId || undefined, false), appliedPublisherData = _k.data, appliedPublisherLoading = _k.isLoading;
    var updateUrlWithFilters = useCallback(function (newFilters) {
        var searchParams = new URLSearchParams();
        if (newFilters.publisherId)
            searchParams.set("publisherId", newFilters.publisherId);
        if (newFilters.publisherPrefixId)
            searchParams.set("publisherPrefixId", newFilters.publisherPrefixId);
        if (newFilters.value)
            searchParams.set("value", newFilters.value);
        if (newFilters.blocked !== undefined)
            searchParams.set("blocked", String(newFilters.blocked));
        appendDateRangeParams(searchParams, newFilters);
        history.push({ search: searchParams.toString() });
    }, [history]);
    // A list opened without URL params shows a publisher user their own numbers.
    var autoFill = usePublisherAutoFill({
        appliesTo: shouldAutoFillPublisher,
        hasFilterParams: hasAnySearchParam,
        canAutoFill: function () { return !temporaryPublisherFilter; },
        onAutoFill: function (publisherSourceId) {
            var autoFilledFilters = __assign(__assign({}, EMPTY_FILTERS), { publisherId: publisherSourceId });
            setAppliedFilters(autoFilledFilters);
            setTemporaryPublisherFilter(publisherSourceId);
            updateUrlWithFilters(autoFilledFilters);
        },
    });
    var cancelAutoFill = autoFill.cancel;
    // Od after Do, or a picker holding text that is not a valid date.
    var hasInvalidDateRange = !areDateRangesValid(temporaryDateRangeFilters) ||
        dateInputs.hasInvalidInput;
    var setTemporaryDateRangeFilter = function (key, value) { return setTemporaryDateRangeFilters(function (prev) {
        var _a;
        return (__assign(__assign({}, prev), (_a = {}, _a[key] = value, _a)));
    }); };
    var applyFilters = useCallback(function (newFilters) {
        cancelAutoFill();
        setAppliedFilters(newFilters);
        updateUrlWithFilters(newFilters);
    }, [cancelAutoFill, updateUrlWithFilters]);
    var handleApplyFilters = function () {
        // An Od after Do is reported in the filter pane and never sent.
        if (hasInvalidDateRange)
            return;
        applyFilters(__assign({ publisherId: temporaryPublisherFilter, publisherPrefixId: temporaryPublisherPrefixFilter, value: temporaryProductNumberFilter, blocked: temporaryBlockedFilter }, temporaryDateRangeFilters));
    };
    var handleClearFilters = function () {
        setTemporaryPublisherFilter("");
        setTemporaryPublisherPrefixFilter("");
        setTemporaryProductNumberFilter("");
        setTemporaryBlockedFilter(undefined);
        setTemporaryDateRangeFilters(EMPTY_DATE_RANGE_FILTERS);
        resetDateInputs();
        cancelAutoFill();
        setAppliedFilters(EMPTY_FILTERS);
        history.push({ search: "" });
    };
    var handlePublisherSearchInput = function (inputValue) {
        if (inputValue !== publisherSearchQuery) {
            setPublisherSearchQuery(inputValue);
        }
    };
    var handlePublisherSelection = function (selectedId) {
        setTemporaryPublisherFilter(selectedId);
        if (!selectedId) {
            setTemporaryPublisherPrefixFilter("");
        }
    };
    var handlePublisherPrefixSelection = function (selectedId) {
        setTemporaryPublisherPrefixFilter(selectedId);
    };
    var isAnyFilterActive = function () {
        return !!(appliedFilters.publisherId ||
            appliedFilters.publisherPrefixId ||
            appliedFilters.value ||
            appliedFilters.blocked !== undefined ||
            hasActiveDateRangeFilter(appliedFilters));
    };
    // Back/forward or a pasted URL: the URL is the applied filters and replaces the draft.
    useEffect(function () {
        var filtersFromUrl = filtersFromSearch(location.search);
        setAppliedFilters(function (prev) {
            return isEqual(prev, filtersFromUrl) ? prev : filtersFromUrl;
        });
        setTemporaryPublisherFilter(filtersFromUrl.publisherId);
        setTemporaryPublisherPrefixFilter(filtersFromUrl.publisherPrefixId);
        setTemporaryProductNumberFilter(filtersFromUrl.value);
        setTemporaryBlockedFilter(filtersFromUrl.blocked);
        setTemporaryDateRangeFilters(pickDateRangeFilters(filtersFromUrl));
        resetDateInputs();
    }, [location.search, resetDateInputs]);
    // The filters the search sends: the publisher by its internal id (its
    // sourceId while it cannot be resolved, as before).
    var filters = useMemo(function () {
        var _a;
        return (__assign(__assign({}, appliedFilters), { publisherId: appliedFilters.publisherId
                ? ((_a = appliedPublisherData === null || appliedPublisherData === void 0 ? void 0 : appliedPublisherData.id) !== null && _a !== void 0 ? _a : appliedFilters.publisherId)
                : "" }));
    }, [appliedFilters, appliedPublisherData === null || appliedPublisherData === void 0 ? void 0 : appliedPublisherData.id]);
    // Nothing is searched until the publisher auto-fill is decided and the
    // applied publisher is resolved — a search without them would be thrown away.
    var filtersReady = !autoFill.isPending &&
        !(appliedFilters.publisherId && appliedPublisherLoading);
    return {
        filters: filters,
        filtersReady: filtersReady,
        /** true when the logged-in user or their roles could not be loaded */
        currentUserLoadFailed: autoFill.isError,
        temporaryPublisherFilter: temporaryPublisherFilter,
        temporaryPublisherPrefixFilter: temporaryPublisherPrefixFilter,
        temporaryProductNumberFilter: temporaryProductNumberFilter,
        temporaryBlockedFilter: temporaryBlockedFilter,
        temporaryDateRangeFilters: temporaryDateRangeFilters,
        hasInvalidDateRange: hasInvalidDateRange,
        dateInputs: dateInputs,
        publisherSearchQuery: publisherSearchQuery,
        selectedPublisherData: selectedPublisherData,
        selectedPublisherLoading: selectedPublisherLoading,
        publisherPrefixesData: publisherPrefixesData,
        publisherPrefixesLoading: publisherPrefixesLoading,
        setTemporaryPublisherFilter: setTemporaryPublisherFilter,
        setTemporaryPublisherPrefixFilter: setTemporaryPublisherPrefixFilter,
        setTemporaryProductNumberFilter: setTemporaryProductNumberFilter,
        setTemporaryBlockedFilter: setTemporaryBlockedFilter,
        setTemporaryDateRangeFilter: setTemporaryDateRangeFilter,
        handleApplyFilters: handleApplyFilters,
        handleClearFilters: handleClearFilters,
        handlePublisherSearchInput: handlePublisherSearchInput,
        handlePublisherSelection: handlePublisherSelection,
        handlePublisherPrefixSelection: handlePublisherPrefixSelection,
        isAnyFilterActive: isAnyFilterActive,
    };
};
